<template>
  <div
    class="radiobutton"
    :class="{ radiobutton_inline: inline, '-disabled': disabled }"
    @click.prevent="onClick"
  >
    <input
      :id="id"
      v-model="radioButtonValue"
      :value="data"
      class="radiobutton__input"
      type="radio"
      :disabled="disabled"
      @click.prevent="onClick"
    />
    <svg
      class="radiobutton__graphic"
      height="20"
      width="24"
      viewBox="0 0 300 300"
    >
      <circle
        r="150"
        cx="150"
        cy="150"
        class="radiobutton__circle"
        :class="{ 'is-on': selected }"
      />
      <circle
        :r="icon ? 150 : 100"
        :style="{
          transform: `scale3d(${selected ? 1 : 0}, ${selected ? 1 : 0}, 1)`
        }"
        cx="150"
        cy="150"
        class="radiobutton__mark"
      />
      <lucide-icon
        v-if="icon"
        x="70"
        y="70"
        width="160"
        height="160"
        :name="icon"
        :style="{ color: selected ? '#fff' : '#555' }"
      />
    </svg>
    <slot :id="id" :selected="selected" :label="label">
      <label
        :for="id"
        class="radiobutton__label"
        :class="{ 'is-selected': selected }"
      >
        {{ label }}
      </label>
    </slot>
    <slot name="after" />
  </div>
</template>

<script setup lang="ts" generic="T">
/**
 * <radio-button
 *  v-model="valueToChange"
 *  :label="''"
 *  data="valueItShouldHaveWhenSelected"
 * >
 */
import { computed } from "vue";
import { v4 as uuid } from "uuid";

import LucideIcon from "@core/ui/LucideIcon.vue";
import type { IconName } from "@core/ui/lucideIcons";

interface Props {
  data: T;
  value: T;
  id?: string;
  label?: string;
  inline?: boolean;
  icon?: IconName;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  id: () => `input-${uuid()}`,
  label: undefined,
  inline: false,
  icon: undefined,
  disabled: false
});

const emit = defineEmits<{
  (event: "update:value", value: T): void;
}>();

const selected = computed(() => {
  return props.value === props.data;
});

const radioButtonValue = computed({
  get() {
    return props.value;
  },
  set() {
    emit("update:value", props.data);
  }
});

function onClick() {
  if (props.disabled) return;
  emit("update:value", props.data);
}
</script>

<style lang="scss">
.radiobutton {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--font-size-label);

  & + & {
    margin-top: 0.4em;
  }

  &__input {
    position: absolute;
    opacity: 0;
    left: 0;
    top: 0;
    pointer-events: none;
  }

  &__graphic {
    display: block;
    flex-shrink: 0;
    height: 20px;
    width: 20px;
    aspect-ratio: 1 / 1;
    border-radius: 9000px;
    overflow: hidden;
    contain: strict;
  }

  &__input:focus:focus-visible ~ &__graphic {
    outline: 2px dotted var(--color-action);
    outline-offset: 1px;
  }

  &__circle {
    fill: transparent;
    stroke: var(--color-text-medium);
    stroke-width: 40px;
    transition: fill 125ms;
    &.is-on {
      stroke: var(--color-action);
    }
  }
  &__mark {
    fill: var(--color-action);
    stroke: none;
    transition: transform 100ms;
    transform-origin: 50% 50%;
    image-rendering: optimizeSpeed;
    image-rendering: -webkit-optimize-contrast; /* Chrome (and Safari) */
    image-rendering: optimize-contrast; /* CSS3 Proposed       */
  }

  &__label {
    display: block;
    width: 100%;
    font-family: var(--font-family-ui);

    &.is-selected {
      color: var(--color-action);
    }
  }

  &.-disabled &__label {
    color: var(--color-text-light);
  }
  &.-disabled &__circle {
    stroke: var(--color-text-light);
  }
  &.-disabled &__mark {
    fill: var(--color-text-light);
  }

  &_inline {
    display: inline-flex;
  }
  &_top {
    align-items: flex-start;
  }
}
</style>
